// frontend/src/perf/metaApiSource.js
import axios from 'axios';
import { extractCoreId, categorizeAdset, categorizeOwner } from './csvParserV2';
import { getMissingDays, setDay, getRowsForRange, listDaysInRange } from './dailyCache';

const API_BASE = process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app';

// Reliable thumbnail source: proxy by ad_id (fresh, non-expiring) with raw URL fallback.
export function videoInfoUrl(adId) {
  return `${API_BASE}/api/meta/video/${adId}`;
}

export function thumbUrl(ad) {
  if (ad && ad.ad_id) return `${API_BASE}/api/meta/thumb/${ad.ad_id}`;
  return (ad && ad.thumbnail_url) || null;
}
const SYNC_CACHE_MS = 5 * 60 * 1000; // 5 minutes
const LS_LAST_SYNC_KEY = 'timbo_meta_last_sync_at';

/**
 * Read the latest daily snapshot from R2 (synced at 9h CET via cron).
 * This is the DEFAULT data source on tab open — no Meta API call, just R2 read.
 * Returns null if no snapshot available.
 */
export async function fetchLatestSnapshot() {
  try {
    const resp = await axios.get(`${API_BASE}/api/meta/snapshot/latest`, { timeout: 10000 });
    if (!resp.data?.ok) return null;
    const snapshot = resp.data.snapshot;
    if (!snapshot?.rows) return null;

    const transformedRows = snapshot.rows.map(transformMetaRow);
    return {
      ok: true,
      rows: transformedRows,
      meta: {
        since: snapshot.since,
        until: snapshot.until,
        synced_at: snapshot.synced_at,
        source: 'r2_snapshot',
        total_ads: snapshot.total_ads,
        attribution_window: snapshot.attribution_window,
        primary_conversion_action: snapshot.primary_conversion_action,
      },
    };
  } catch (err) {
    console.warn('[snapshot] Failed to load latest snapshot:', err.message);
    return null;
  }
}

/**
 * Fetch les insights Meta pour une plage de dates donnée.
 * @param {string} since - YYYY-MM-DD
 * @param {string} until - YYYY-MM-DD
 * @returns {Promise<{ok: boolean, rows?: Array, error?: string, meta?: Object}>}
 */
export async function fetchMetaInsights(since, until) {
  try {
    const url = `${API_BASE}/api/meta/insights/ads?since=${since}&until=${until}`;
    const resp = await axios.get(url, { timeout: 120000 }); // 2 min timeout pour les gros ranges

    if (!resp.data?.ok) {
      return { ok: false, error: resp.data?.error || 'Unknown error from /api/meta/insights/ads' };
    }

    const rawData = resp.data.data || [];
    const transformedRows = rawData.map(transformMetaRow);

    return { ok: true, rows: transformedRows, meta: resp.data.meta };
  } catch (err) {
    const msg = err.response?.data?.error || err.message;
    return { ok: false, error: typeof msg === 'string' ? msg : JSON.stringify(msg) };
  }
}

/**
 * Transforme une row de l'API Meta vers le format attendu par aggregateByCorIdAndCategory.
 * Champs requis par l'aggregator: core_id, adset_category, ad_owner, ad_name, adset_name,
 * delivery, results, spend, impressions, reach, date_start, date_end.
 */
function transformMetaRow(metaRow) {
  // app_custom_event.fb_mobile_purchase = vrai KPI conversion PhotoRoom (matche "Résultats" Meta Ads Manager)
  // Fallback hiérarchique si l'event custom n'est pas tracké sur cet ad :
  //   1. fb_mobile_purchase (custom, le plus fiable côté PhotoRoom)
  //   2. omni_purchase (cumul tous canaux)
  //   3. purchase (legacy)
  // No mobile_app_install fallback: a missing purchase is 0, not an install (same rule as
  // backend/askTimbo.js). The old fallback counted installs as purchases on ads with no
  // purchase (audit 31/05/26: 337 purchases vs 5,701 installs) -> €0.40 "CPA", inflated winners.
  const fbMobilePurchase = getActionValue(metaRow.actions, 'app_custom_event.fb_mobile_purchase');
  const omniPurchase = getActionValue(metaRow.actions, 'omni_purchase');
  const purchase = getActionValue(metaRow.actions, 'purchase');
  const conversions = fbMobilePurchase || omniPurchase || purchase || 0;

  const adName = metaRow.ad_name || '';
  const adsetName = metaRow.adset_name || '';

  return {
    ad_name: adName,
    adset_name: adsetName,
    campaign_name: metaRow.campaign_name || '',
    delivery: 'Active',
    results: conversions,
    achats: conversions,
    spend: parseFloat(metaRow.spend || 0),
    impressions: parseInt(metaRow.impressions || 0, 10),
    reach: parseInt(metaRow.reach || 0, 10),
    date_start: metaRow.date_start || '',
    date_end: metaRow.date_stop || metaRow.date_end || '',
    date_stop: metaRow.date_stop || '',
    ad_id: metaRow.ad_id,
    thumbnail_url: metaRow.thumbnail_url || null,
    video_views: metaRow.video_views || 0,
    ctr: parseFloat(metaRow.ctr || 0),
    cpm: parseFloat(metaRow.cpm || 0),
    // v4.02 — leading indicators: clicks + video quartiles. Backend already
    // returns these per ad (server.js normalizeInsightRow); they used to be
    // thrown away in transform + aggregator.
    clicks: parseInt(metaRow.clicks || 0, 10),
    video_p25: parseInt(metaRow.video_p25 || 0, 10),
    video_p50: parseInt(metaRow.video_p50 || 0, 10),
    video_p75: parseInt(metaRow.video_p75 || 0, 10),
    video_p100: parseInt(metaRow.video_p100 || 0, 10),
    core_id: extractCoreId(adName),
    adset_category: categorizeAdset(adsetName),
    ad_owner: categorizeOwner(adName),
  };
}

function getActionValue(actions, actionType) {
  if (!Array.isArray(actions)) return 0;
  const found = actions.find(a => a.action_type === actionType);
  if (!found) return 0;
  // Backend Meta API now requests action_attribution_windows=['7d_click'] (v3.98.1+).
  // Meta returns 'value' = cumul toutes attributions (ce que Meta Ads Manager affiche).
  // Le champ '7d_click' contient la même valeur calculée sur la fenêtre demandée.
  // On extrait 'value' en priorité (matche le "Résultats" colonne Ads Manager).
  // Fallback hiérarchique sur '7d_click' puis '1d_click' (anciens caches éventuels).
  return parseInt(found.value || found['7d_click'] || found['1d_click'] || 0, 10);
}

/**
 * Calcule since/until depuis un range UI.
 * Périodes supportées : 1d, 3d, 7d, 14d, 30d, 90d, 6m, all
 */
export function rangeToDates(range) {
  // Object input: pass-through (from new DateRangePicker)
  if (range && typeof range === 'object' && range.since && range.until) {
    return { since: range.since, until: range.until };
  }
  const today = new Date();
  const until = today.toISOString().slice(0, 10);

  if (range === 'all') {
    return { since: '2025-09-01', until };
  }
  if (range === '6m') {
    const since = new Date(today);
    since.setMonth(since.getMonth() - 6);
    return { since: since.toISOString().slice(0, 10), until };
  }

  const days = { '1d': 1, '3d': 3, '7d': 7, '14d': 14, '30d': 30, '90d': 90 }[range];
  if (days) {
    const since = new Date(today);
    since.setDate(since.getDate() - (days - 1));
    return { since: since.toISOString().slice(0, 10), until };
  }

  // Fallback : 30 jours
  const since = new Date(today);
  since.setDate(since.getDate() - 29);
  return { since: since.toISOString().slice(0, 10), until };
}

/**
 * Gestion du cache de sync (localStorage).
 */
export function getLastSyncAt() {
  const raw = localStorage.getItem(LS_LAST_SYNC_KEY);
  if (!raw) return null;
  const ts = parseInt(raw, 10);
  return Number.isFinite(ts) ? ts : null;
}

export function setLastSyncAt(ts = Date.now()) {
  localStorage.setItem(LS_LAST_SYNC_KEY, String(ts));
}

export function isSyncStale() {
  const last = getLastSyncAt();
  if (!last) return true;
  return (Date.now() - last) > SYNC_CACHE_MS;
}

export function timeSinceLastSync() {
  const last = getLastSyncAt();
  if (!last) return null;
  const sec = Math.floor((Date.now() - last) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/**
 * Fetch one day from the backend /api/meta/insights/ads/by-day endpoint.
 * Returns raw Meta rows (not transformed yet).
 */
async function fetchOneDayRaw(date, withThumbnails) {
  const url = `${API_BASE}/api/meta/insights/ads/by-day?date=${date}` + (withThumbnails ? '&with_thumbnails=true' : '');
  const resp = await axios.get(url, { timeout: 60000 });
  if (!resp.data?.ok) {
    throw new Error(resp.data?.error || `Day ${date} failed`);
  }
  return resp.data.rows || [];
}

/**
 * Incremental fetch with day-by-day cache and 5-parallel batches.
 * - Checks cache via dailyCache.getMissingDays()
 * - Fetches only missing/stale days
 * - Stores each successful day in cache
 * - Returns concatenated + transformed rows for the full range
 *
 * opts: { withThumbnails?: bool, onProgress?: (done, total, phase) => void, concurrency?: number }
 */
export async function fetchMetaInsightsIncremental(since, until, opts = {}) {
  const withThumbnails = !!opts.withThumbnails;
  const onProgress = opts.onProgress || (() => {});
  const concurrency = Math.max(1, opts.concurrency || 5);

  try {
    const allDays = listDaysInRange(since, until);
    const missing = await getMissingDays(since, until);
    const cachedCount = allDays.length - missing.length;
    onProgress(cachedCount, allDays.length, 'starting');

    const failures = [];
    // Parallelize in chunks of `concurrency`
    for (let i = 0; i < missing.length; i += concurrency) {
      const batch = missing.slice(i, i + concurrency);
      const results = await Promise.allSettled(batch.map(date => fetchOneDayRaw(date, withThumbnails)));
      for (let j = 0; j < results.length; j++) {
        const r = results[j];
        const date = batch[j];
        if (r.status === 'fulfilled') {
          try { await setDay(date, r.value); }
          catch (e) { console.warn('[meta-incremental] setDay failed for', date, e); }
        } else {
          failures.push({ date, error: r.reason?.message || String(r.reason) });
          console.warn('[meta-incremental] fetch failed for', date, r.reason);
        }
      }
      onProgress(cachedCount + Math.min(i + concurrency, missing.length), allDays.length, 'fetching');
    }

    // Concatenate everything from cache (cached + newly stored)
    const rawRows = await getRowsForRange(since, until);
    const transformedRows = rawRows.map(transformMetaRow);

    onProgress(allDays.length, allDays.length, 'done');

    return {
      ok: failures.length === 0,
      rows: transformedRows,
      meta: {
        since, until,
        total_days: allDays.length,
        cached_days: cachedCount,
        fetched_days: missing.length - failures.length,
        failed_days: failures.length,
        failures,
        attribution_window: '7d_click',
        primary_conversion_action: 'app_custom_event.fb_mobile_purchase',
      },
      error: failures.length > 0
        ? `${failures.length}/${missing.length} day(s) failed (see meta.failures)`
        : null,
    };
  } catch (err) {
    return { ok: false, error: err.message || String(err) };
  }
}


/**
 * Background retry of failed days. Silent — no UI feedback.
 * Up to 3 attempts with 5s delays. Stores successful days in cache.
 * Returns the list of dates that still failed after 3 attempts.
 */
export async function retryFailedDaysInBackground(failedDates, opts = {}) {
  const withThumbnails = !!opts.withThumbnails;
  let stillFailed = [...(failedDates || [])];

  for (let attempt = 1; attempt <= 3; attempt++) {
    if (stillFailed.length === 0) break;
    // Wait 5s between attempts (and before first to let Meta breathe)
    await new Promise(r => setTimeout(r, 5000));
    const currentBatch = [...stillFailed];
    const nextRound = [];
    for (const date of currentBatch) {
      try {
        const rows = await fetchOneDayRaw(date, withThumbnails);
        await setDay(date, rows);
        console.log(`[meta-retry] Day ${date} succeeded on attempt ${attempt}`);
      } catch (err) {
        nextRound.push(date);
        console.warn(`[meta-retry] Day ${date} still failing on attempt ${attempt}:`, err.message);
      }
    }
    stillFailed = nextRound;
  }

  return stillFailed;
}

/**
 * Re-read all cached rows for [since, until] and return them transformed
 * via transformMetaRow. Used after a background retry to refresh the UI.
 */
export async function reaggregateFromCache(since, until) {
  const rawRows = await getRowsForRange(since, until);
  return rawRows.map(transformMetaRow);
}
